import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { LoaderSkeleton } from '@utilities';
import AttributeOptions from './components/AttributeOptions';

import styles from './_index.module.scss';

const AttributeOptionsResults = ({
  activePrompt,
  activePromptAnswers,
  isCustomLoading,
  isLoading,
  onAnswerChange,
  results,
}) => {
  const { attributes = [] } = activePrompt || {};

  const regularAttributes = useMemo(
    () =>
      attributes
        .filter((attribute) => !attribute?.isCustom)
        .map((attribute) => {
          const data = results?.filter((result) => result?.attributeId === attribute?.id);
          return { data, id: attribute?.id, name: attribute?.name };
        }),
    [attributes, results]
  );

  const customAttributes = useMemo(
    () =>
      attributes
        .filter((attribute) => attribute?.isCustom)
        .map((attribute) => {
          const data = results?.filter((result) => result?.attributeId === attribute?.id);
          return { data, id: attribute?.id, name: attribute?.name };
        }),
    [attributes, results]
  );

  const hasCustomResults = customAttributes.some((attr) => attr?.data?.length > 0);
  const hasResults = regularAttributes.some((attr) => attr?.data?.length > 0);

  const renderLoader = () => {
    return (
      <LoaderSkeleton height={115}>
        <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
        <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
        <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        <rect height="15" rx="2" ry="2" width="225" x="0" y="100" />
      </LoaderSkeleton>
    );
  };

  return (
    <div className={styles['attribute-options-results-wrapper']}>
      <h4 className={styles['attribute-options-sub-header']}>{activePrompt?.name}</h4>
      {isLoading && renderLoader()}
      {!isLoading &&
        hasResults &&
        regularAttributes?.map((attribute, index) => {
          if (!attribute?.data?.length) return null;

          return (
            <div className={styles['attribute-options-results']} key={`attribute-regular-${index}`}>
              <span className={styles['attribute-options-results-left-column']}>
                {attribute?.name}
              </span>
              <div className={styles['attribute-options-results-right-column']}>
                <AttributeOptions
                  activePrompt={activePrompt}
                  activePromptAnswers={activePromptAnswers}
                  max={activePrompt?.maxValues}
                  onAnswerChange={onAnswerChange}
                  options={attribute?.data}
                />
              </div>
            </div>
          );
        })}
      {!isLoading && !hasResults && <div>No results found.</div>}
      <h4 className={styles['attribute-options-sub-header']}>User Custom Groups</h4>
      {isCustomLoading && renderLoader()}
      {!isCustomLoading && hasCustomResults && (
        <div className={styles['attribute-options-results-custom-group']}>
          {customAttributes.map((attribute, index) => {
            if (!attribute?.data?.length) return null;

            return (
              <div
                className={styles['attribute-options-results']}
                key={`attribute-custom-${index}`}
              >
                <span className={styles['attribute-options-results-left-column']}>
                  {attribute?.name}
                </span>
                <div className={styles['attribute-options-results-right-column']}>
                  <AttributeOptions
                    activePrompt={activePrompt}
                    activePromptAnswers={activePromptAnswers}
                    max={activePrompt?.maxValues}
                    onAnswerChange={onAnswerChange}
                    options={attribute?.data}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!isCustomLoading && !hasCustomResults && <div>No custom results found.</div>}
    </div>
  );
};

AttributeOptionsResults.propTypes = {
  activePrompt: PropTypes.object,
  activePromptAnswers: PropTypes.array,
  isCustomLoading: PropTypes.bool,
  isLoading: PropTypes.bool,
  onAnswerChange: PropTypes.func,
  results: PropTypes.array,
};

export default AttributeOptionsResults;
